// Pure-ish cache reducers: apply one queued mutation to the cached GET responses.
// `store` exposes get/set/keys over cache paths. Must be idempotent (re-applied over fresh data).
const now = () => new Date().toISOString();
const TRASH_DAYS = 3;

const itemListKeys = (store) => store.keys().filter((k) => /^\/titles\/[^/]+\/items$/.test(k));
const itemsKey = (tid) => `/titles/${tid}/items`;

function patchTitle(store, tid, fn) {
  const list = store.get("/titles");
  if (Array.isArray(list)) store.set("/titles", list.map((t) => (t.title_id === tid ? fn(t) : t)));
}

function addItem(store, tid, item) {
  const list = store.get(itemsKey(tid));
  if (Array.isArray(list) && !list.some((x) => x.item_id === item.item_id)) {
    store.set(itemsKey(tid), [...list, item]);
    patchTitle(store, tid, (t) => ({
      ...t,
      item_count: (t.item_count || 0) + 1,
      last_message: item.type === "file" ? (item.content || `[file] ${item.file_name}`) : (item.content || item.url || "").slice(0, 80),
      last_activity: now(),
    }));
  }
}

function removeItem(store, id) {
  for (const k of itemListKeys(store)) {
    const list = store.get(k);
    const hit = Array.isArray(list) && list.find((x) => x.item_id === id);
    if (hit) {
      store.set(k, list.filter((x) => x.item_id !== id));
      patchTitle(store, hit.title_id, (t) => ({ ...t, item_count: Math.max(0, (t.item_count || 1) - 1) }));
      return hit;
    }
  }
  return null;
}

function findItem(store, id) {
  for (const k of itemListKeys(store)) {
    const hit = (store.get(k) || []).find?.((x) => x.item_id === id);
    if (hit) return hit;
  }
  return null;
}

function reorder(list, ids, idOf) {
  if (!Array.isArray(list) || !Array.isArray(ids)) return list;
  const pos = new Map(ids.map((id, i) => [id, i]));
  return [...list].sort((a, b) => (pos.get(idOf(a)) ?? 1e9) - (pos.get(idOf(b)) ?? 1e9));
}

function addTrash(store, entry) {
  const trash = store.get("/trash");
  if (Array.isArray(trash) && !trash.some((e) => e.id === entry.id)) store.set("/trash", [entry, ...trash]);
}

export function reduce(op, store, resp) {
  const { method, path } = op;
  const body = op.body || {};
  const seg = path.split("/").filter(Boolean);

  if (path === "/titles" && method === "POST") {
    const t = resp?.title_id ? resp : { title_id: body.title_id, name: (body.name || "").trim() || "Untitled", color: body.color || "#2563EB", item_count: 0, last_message: null, created_at: now(), last_activity: now() };
    const list = store.get("/titles");
    if (Array.isArray(list) && !list.some((x) => x.title_id === t.title_id)) store.set("/titles", [t, ...list]);
    if (store.get(itemsKey(t.title_id)) === undefined) store.set(itemsKey(t.title_id), []);
    return t;
  }
  if (path === "/titles/reorder") {
    const list = store.get("/titles");
    if (Array.isArray(list)) store.set("/titles", reorder(list, body.title_ids, (t) => t.title_id));
    return { ok: true };
  }
  if (seg[0] === "titles" && seg.length === 2) {
    const tid = seg[1];
    const title = (store.get("/titles") || []).find?.((t) => t.title_id === tid);
    if (method === "PATCH") {
      patchTitle(store, tid, (t) => ({ ...t, ...body, ...(resp || {}) }));
      return { ...(title || {}), ...body, ...(resp || {}) };
    }
    if (method === "DELETE") {
      const list = store.get("/titles");
      if (Array.isArray(list)) store.set("/titles", list.filter((t) => t.title_id !== tid));
      if (title) addTrash(store, { kind: "title", id: tid, title_id: tid, name: title.name, color: title.color, items_count: title.item_count || 0, deleted_at: now(), days_remaining: TRASH_DAYS, _title: title });
      return { ok: true };
    }
  }
  if (seg[0] === "titles" && seg[2] === "items" && seg.length === 3) {
    const item = resp?.item_id ? resp : { item_id: body.item_id, title_id: seg[1], type: body.type, content: body.content || "", url: body.url || null, preview: null, created_at: now() };
    addItem(store, seg[1], item);
    return item;
  }
  if (seg[0] === "titles" && seg[3] === "reorder") {
    const list = store.get(itemsKey(seg[1]));
    if (Array.isArray(list)) store.set(itemsKey(seg[1]), reorder(list, body.item_ids, (x) => x.item_id));
    return { ok: true };
  }
  if (seg[0] === "titles" && seg[2] === "uploads") {
    const f = op.form || {};
    const item = resp?.item_id ? resp : {
      item_id: f.item_id, title_id: seg[1], type: "file", content: f.caption || "", url: null, preview: null,
      file_name: f.file_name, mime_type: f.mime_type, size: f.size, is_image: (f.mime_type || "").startsWith("image/"),
      group_id: f.group_id || null, is_deleted: false, created_at: now(), _local: true,
    };
    if (item.item_id) addItem(store, seg[1], item);
    return item;
  }
  if (seg[0] === "items" && seg.length === 2) {
    const id = seg[1];
    if (method === "DELETE") {
      const hit = removeItem(store, id);
      if (hit) {
        const title = (store.get("/titles") || []).find?.((t) => t.title_id === hit.title_id);
        addTrash(store, { kind: "item", id, ...hit, title_name: title?.name, title_color: title?.color, deleted_at: now(), days_remaining: TRASH_DAYS, _item: hit });
      }
      return { ok: true };
    }
    const cur = findItem(store, id);
    if (!cur) return resp || { item_id: id, ...body };
    let next = { ...cur };
    if (body.content !== undefined) next.content = body.content;
    if (body.color !== undefined) next.color = body.color || undefined;
    if (body.url !== undefined) {
      if (!body.url) next = { ...next, type: "note", url: null, preview: null };
      else if (body.url !== cur.url) next = { ...next, type: "link", url: body.url, preview: null };
    }
    if (resp?.item_id) next = resp;
    if (body.title_id && body.title_id !== cur.title_id) {
      removeItem(store, id);
      addItem(store, body.title_id, { ...next, title_id: body.title_id });
    } else {
      const k = itemsKey(cur.title_id);
      store.set(k, (store.get(k) || []).map((x) => (x.item_id === id ? next : x)));
    }
    return next;
  }
  if (seg[0] === "trash") {
    const trash = store.get("/trash") || [];
    if (path === "/trash/empty") { store.set("/trash", []); return { ok: true, deleted: trash.length }; }
    const entry = trash.find?.((e) => e.id === seg[1]);
    if (Array.isArray(store.get("/trash"))) store.set("/trash", trash.filter((e) => e.id !== seg[1]));
    if (seg[2] === "restore" && entry) {
      const list = store.get("/titles");
      if (entry._title || entry.kind === "title") {
        const t = entry._title || { title_id: entry.id, name: entry.name, color: entry.color, item_count: entry.items_count || 0, last_activity: now() };
        if (Array.isArray(list) && !list.some((x) => x.title_id === t.title_id)) store.set("/titles", [t, ...list]);
      } else if (entry._item || entry.kind === "item") {
        const { kind, id, title_name, title_color, deleted_at, days_remaining, _item, ...fields } = entry;
        const it = _item || { ...fields, item_id: entry.item_id || id };
        if (it.title_id) addItem(store, it.title_id, { ...it, is_deleted: false });
      }
    }
    return { ok: true };
  }
  if (seg[0] === "reminders") {
    const list = store.get("/reminders");
    if (method === "POST") {
      const r = resp?.reminder_id ? resp : { recurrence: "none", time: "09:00", sound: "chime", ...body, sent: false, created_at: now() };
      if (Array.isArray(list) && !list.some((x) => x.reminder_id === r.reminder_id)) store.set("/reminders", [...list, r]);
      return r;
    }
    if (method === "PUT") {
      const merged = { ...(list || []).find?.((x) => x.reminder_id === seg[1]), ...body, ...(resp || {}) };
      if (Array.isArray(list)) store.set("/reminders", list.map((x) => (x.reminder_id === seg[1] ? merged : x)));
      return merged;
    }
    if (Array.isArray(list)) store.set("/reminders", list.filter((x) => x.reminder_id !== seg[1]));
    return { ok: true };
  }
  if (path === "/push/prefs") {
    const v = { reminder_muted: !!body.reminder_muted };
    store.set("/push/prefs", v);
    return v;
  }
  if (path === "/user/settings") {
    const v = { ...(store.get("/user/settings") || {}), ...body };
    store.set("/user/settings", v);
    return v;
  }
  return { ok: true };
}
